import { getNews, deleteNews, updateNews, addNews } from '@/adapters/master';
import { INews, INewsRes, IApiResponse } from '@/interfaces/master';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Flex,
  notification,
  Radio,
  Table,
  TableColumnsType,
  Input,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { uniqueId } from 'lodash';
import dayjs from 'dayjs';

interface IBatchRejection {
  status: 'rejected' | 'fulfilled';
  reason?: { errors?: { errorMessage?: string } };
}

const getErrorMessage = (r: unknown): string => {
  if (r instanceof Error) return r.message;
  if (typeof r === 'object' && r !== null && 'message' in r)
    return (r as { message: string }).message;
  return 'เกิดข้อผิดพลาดในการดำเนินการ';
};

const NewsPage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<INews[]>([]);
  const [selected, setSelected] = useState<INews[]>([]);
  const [tab, setTab] = useState<'create' | 'update'>('create');
  const [searchText, setSearchText] = useState('');

  const { data, refetch, isFetching } = useQuery<INewsRes, Error>({
    queryKey: ['get-news'],
    queryFn: getNews,
    retry: 0,
  });

  const notifyBatchErrors = (
    title: string,
    res: IApiResponse,
    originalData: Partial<INews>[],
  ) => {
    const errors: string[] = [];
    if (Array.isArray(res.data)) {
      (res.data as IBatchRejection[]).forEach((result, index) => {
        if (result.status === 'rejected') {
          const failedCode =
            originalData[index]?.NEWS_CODE || `Index: ${index}`;
          errors.push(
            `${failedCode}: ${result.reason?.errors?.errorMessage || 'ไม่ทราบสาเหตุ'}`,
          );
        }
      });
    } else if (res.status !== 'OK' && res.status !== 'fulfilled')
      errors.push(res.message || 'เกิดข้อผิดพลาดจากเซิร์ฟเวอร์');

    if (errors.length > 0) {
      notification.error({
        message: title,
        style: { width: '500px' },
        description: (
          <ul
            style={{
              paddingLeft: '15px',
              margin: 0,
              maxHeight: '250px',
              overflowY: 'auto',
            }}
          >
            {errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        ),
      });
      return true;
    }
    return false;
  };

  const actionNewsMutation = useMutation<IApiResponse, Error, Partial<INews>[]>(
    {
      mutationFn: payload =>
        tab === 'create' ? addNews(payload) : updateNews(payload),
      onSuccess: (res, variables) => {
        if (
          !notifyBatchErrors(
            tab === 'create' ? 'Sync News ไม่สำเร็จ' : 'Update News ไม่สำเร็จ',
            res,
            variables,
          )
        ) {
          notification.success({
            message:
              tab === 'create' ? 'Sync News สำเร็จ' : 'Update News สำเร็จ',
          });
        }
        refetch();
      },
      onError: r => notification.error({ message: getErrorMessage(r) }),
    },
  );

  const actionDeleteNews = useMutation<IApiResponse, Error, Partial<INews>[]>({
    mutationFn: payload => deleteNews(payload),
    onSuccess: (res, variables) => {
      if (!notifyBatchErrors('ลบข้อมูลไม่สำเร็จ', res, variables)) {
        notification.success({ message: 'Delete News สำเร็จ' });
      }
      refetch();
    },
    onError: r => notification.error({ message: getErrorMessage(r) }),
  });

  const filteredData = useMemo(() => {
    if (!searchText) return selected;
    const lowerSearch = searchText.toLowerCase();
    return selected.filter(
      item =>
        item.NEWS_CODE?.toLowerCase().includes(lowerSearch) ||
        item.NEWS_TYPE?.toLowerCase().includes(lowerSearch) ||
        item.NEWS_HLINE?.toLowerCase().includes(lowerSearch) ||
        item.NEWS_CONTENT?.toLowerCase().includes(lowerSearch),
    );
  }, [selected, searchText]);

  useEffect(() => {
    const list = tab === 'create' ? data?.insert : data?.update;
    setSelected(list?.map(c => ({ ...c, _key: uniqueId('news-') })) || []);
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, [tab, data]);

  const columns: TableColumnsType<INews> = useMemo(
    () => [
      {
        title: 'ลำดับ',
        width: 60,
        align: 'center',
        render: (_, __, i) => <b>{i + 1}</b>,
      },
      {
        title: 'เอกสาร',
        dataIndex: 'NEWS_CODE',
        width: 140,
        sorter: (a, b) => (a.NEWS_CODE || '').localeCompare(b.NEWS_CODE || ''),
        filters: Array.from(
          new Set(selected.map(i => i.NEWS_CODE).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (v, r) => r.NEWS_CODE === v,
        filterSearch: true,
      },
      {
        title: 'วันที่',
        dataIndex: 'NEWS_DATE',
        width: 120,
        render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
        sorter: (a, b) => dayjs(a.NEWS_DATE).unix() - dayjs(b.NEWS_DATE).unix(),
        filters: Array.from(
          new Set(
            selected
              .map(i => dayjs(i.NEWS_DATE).format('DD/MM/YYYY'))
              .filter(Boolean),
          ),
        )
          .sort()
          .map(v => ({ text: v, value: v })),
        onFilter: (v, r) => dayjs(r.NEWS_DATE).format('DD/MM/YYYY') === v,
        filterSearch: true,
      },
      {
        title: 'ประเภทข่าว',
        dataIndex: 'NEWS_TYPE',
        width: 150,
        sorter: (a, b) => (a.NEWS_TYPE || '').localeCompare(b.NEWS_TYPE || ''),
        filters: Array.from(
          new Set(selected.map(i => i.NEWS_TYPE).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (v, r) => r.NEWS_TYPE === v,
        filterSearch: true,
      },
      {
        title: 'หัวข้อข่าว',
        dataIndex: 'NEWS_HLINE',
        ellipsis: true,
        width: 200,
        sorter: (a, b) =>
          (a.NEWS_HLINE || '').localeCompare(b.NEWS_HLINE || ''),
        filters: Array.from(
          new Set(selected.map(i => i.NEWS_HLINE).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (v, r) => r.NEWS_HLINE === v,
        filterSearch: true,
      },
      {
        title: 'เนื้อหาข่าวสาร',
        dataIndex: 'NEWS_CONTENT',
        width: 400,
        sorter: (a, b) =>
          (a.NEWS_CONTENT || '').localeCompare(b.NEWS_CONTENT || ''),
        filters: Array.from(
          new Set(selected.map(i => i.NEWS_CONTENT).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (v, r) => r.NEWS_CONTENT === v,
        filterSearch: true,
        render: text => (
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {text}
          </div>
        ),
      },
    ],
    [selected],
  );

  const isPending = actionNewsMutation.isPending || actionDeleteNews.isPending;

  return (
    <div style={{ paddingBottom: '20px' }}>
      <Flex justify="space-between" align="center" className="mb-3">
        <Radio.Group
          value={tab}
          onChange={e => setTab(e.target.value)}
          buttonStyle="solid"
        >
          <Radio.Button value="create">เพิ่มข่าว</Radio.Button>
          <Radio.Button value="update">แก้ไข/ลบ</Radio.Button>
        </Radio.Group>
        <Flex gap={12}>
          <Input.Search
            placeholder="ค้นหา (เอกสาร / ประเภท / หัวข้อ / เนื้อหา)"
            allowClear
            onSearch={setSearchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 400 }}
          />
          {tab === 'update' && (
            <button
              type="button"
              className="btn btn-outline-danger"
              disabled={!selectedRowKeys.length || isPending}
              onClick={() => actionDeleteNews.mutate(selectedRows)}
            >
              {actionDeleteNews.isPending ? 'กำลังลบ...' : 'ลบข้อมูล News'}
            </button>
          )}
          <button
            type="button"
            className="btn btn-success"
            style={{ minWidth: 120 }}
            disabled={!selectedRowKeys.length || isPending}
            onClick={() => actionNewsMutation.mutate(selectedRows)}
          >
            {isPending
              ? 'กำลังดำเนินการ...'
              : tab === 'create'
                ? 'ส่งข้อมูลไป OTOD >'
                : 'แก้ไขข้อมูลที่ OTOD >'}
          </button>
        </Flex>
      </Flex>
      <Table<INews>
        rowSelection={{
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
          },
        }}
        columns={columns}
        dataSource={filteredData}
        loading={isFetching}
        size="small"
        bordered
        rowKey="_key"
        pagination={false}
        scroll={{ x: 1300 }}
        sticky={{ offsetHeader: 0 }}
        locale={{ emptyText: 'ไม่พบรายการข่าว' }}
      />
    </div>
  );
};

export default NewsPage;
